/**
 * Header/Footer Section Component
 * Renders header or footer container with hover effects
 */

import type { ContainerElement } from '../../types/template.types';
import { ContainerRenderer } from '../renderers/container-renderer';

interface HeaderFooterSectionProps {
  container: ContainerElement;
  type: 'header' | 'footer';
  height: number;
  pageWidth: number;
  pageHeight: number;
  containerPath: number[];
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onInsertClick: (
    containerPath: number[],
    rowIndex: number,
    position: 'above' | 'below',
    cellRow?: number,
    cellColumn?: number
  ) => void;
  onContainerChange: (container: ContainerElement) => void;
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  topMargin: number;
  bottomMargin: number;
  leftMargin: number;
  rightMargin: number;
  rootContainerIds?: string[];
}

export function HeaderFooterSection({
  container,
  type,
  height,
  pageWidth,
  pageHeight,
  containerPath,
  selectedElementId,
  onSelectElement,
  onInsertClick,
  onContainerChange,
  fontFamily,
  fontSize,
  fontColor,
  topMargin,
  bottomMargin,
  leftMargin,
  rightMargin,
  rootContainerIds = [],
}: HeaderFooterSectionProps) {
  const isHeader = type === 'header';
  
  // Calculate content width (like root containers)
  const contentWidth = pageWidth - leftMargin - rightMargin;
  
  const deleteFromContainer = (container: ContainerElement, elementId: string): ContainerElement => {
    const children = Array.isArray(container.children) ? container.children : [];
    return {
      ...container,
      children: children
        .filter(child => child.element.id !== elementId)
        .map(child => ({
          ...child,
          element: child.element.type === 'container'
            ? deleteFromContainer(child.element as ContainerElement, elementId)
            : child.element
        }))
    };
  };

  return (
    <div 
      className="group relative" 
      style={{ 
        backgroundColor: '#ffffff', // Always white for print preview
        borderTop: isHeader ? undefined : '2px solid transparent',
        borderBottom: isHeader ? '2px solid transparent' : undefined,
        transition: 'border-color 0.2s',
        width: contentWidth,
        height: `${height}px`,
        position: isHeader ? undefined : 'absolute',
        top: isHeader ? undefined : `${pageHeight - height}px`,
        left: leftMargin,
        zIndex: 500,
        pointerEvents: 'auto',
      }}
      onMouseEnter={(e) => {
        if (isHeader) {
          e.currentTarget.style.borderBottom = '2px solid var(--primary)';
        } else {
          e.currentTarget.style.borderTop = '2px solid var(--primary)';
        }
      }}
      onMouseLeave={(e) => {
        if (isHeader) {
          e.currentTarget.style.borderBottom = '2px solid transparent';
        } else {
          e.currentTarget.style.borderTop = '2px solid transparent';
        }
      }}
    >
      <div className="absolute top-2 left-2 z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded shadow-lg">
          {isHeader ? 'Kopfzeile' : 'Fußzeile'}
        </span>
      </div>
      <ContainerRenderer
        container={{ 
          ...container,
          // Set height to fill entire margin area
          height,
          // Force vertical stretch for header/footer
          verticalAlign: 'stretch',
          // Clear width if horizontal stretch
          ...(container.horizontalAlign === 'stretch' ? { width: contentWidth } : {})
        }}
        containerPath={containerPath}
        selectedElementId={selectedElementId}
        onSelectElement={onSelectElement}
        onInsertClick={(path, rowIndex, position, cellRow, cellColumn) => {
          const pathStart = containerPath[0];
          if (pathStart !== undefined) {
            onInsertClick([pathStart, ...path.slice(1)], rowIndex, position, cellRow, cellColumn);
          }
        }}
        onDelete={(elementId) => {
          onContainerChange(deleteFromContainer(container, elementId));
          onSelectElement(null);
        }}
        fontFamily={fontFamily}
        fontSize={fontSize}
        fontColor={fontColor}
        isDeletable={false}
        topMargin={topMargin}
        bottomMargin={bottomMargin}
        rootContainerIds={rootContainerIds}
      />
    </div>
  );
}
